import type { PortMessage } from '../../../../types/PortMessage';
import { CategoryState } from '../../../../types/ReachInformationMessage';
import { formatPort } from '../../../common/helpers/formatPort.helper';
import type { Schedule } from '../../../common/hooks/useSchedules';
import type { NoSchedulePortOption, PortOption, SchedulePortOption } from '../PortDetailSelect';
import { PortOptionType } from '../PortOptionType';

interface KeyPort {
    portId: string;
    vesselLimitations?: string[];
}

const orderScheduleOptionsByReach = (options: SchedulePortOption[]): SchedulePortOption[] => {
    const orderedOptions = [
        ...options.filter((option) => option.scheduleEntry.reachStatus !== CategoryState.OUT_OF_REACH),
        ...options.filter((option) => option.scheduleEntry.reachStatus === CategoryState.OUT_OF_REACH),
    ];
    const firstOutOfReachIndex = orderedOptions.findIndex(
        (option) => option.scheduleEntry.reachStatus === CategoryState.OUT_OF_REACH
    );

    return orderedOptions.map((option, index) => ({
        ...option,
        showReachSeparator: index === firstOutOfReachIndex,
    }));
};

export const toScheduleOptions = (schedule: Schedule[], vesselId?: string): PortOption[] => {
    const options: SchedulePortOption[] = schedule
        .filter((scheduleEntry) => !!scheduleEntry.port)
        .map((scheduleEntry) => ({
            type: PortOptionType.SCHEDULE,
            label: formatPort({ port: scheduleEntry.port!, short: true }),
            value: scheduleEntry.id,
            port: scheduleEntry.port!,
            vesselId,
            scheduleEntry,
            schedulesLength: schedule.length,
            showReachSeparator: false,
        }));

    return orderScheduleOptionsByReach(options);
};

export const toNoScheduleOptions = (ports: PortMessage[], vesselId?: string): PortOption[] =>
    ports.map((port) => ({
        type: PortOptionType.NO_SCHEDULE,
        label: formatPort({ port, short: true }),
        value: port.id,
        port,
        vesselId,
    }));

export const getKeyPortIds = (keyPorts: KeyPort[], vesselId?: string): string[] =>
    keyPorts
        .filter((keyPort) => {
            if (!vesselId || !keyPort.vesselLimitations?.length) {
                return true;
            }
            return keyPort.vesselLimitations.includes(vesselId);
        })
        .map((keyPort) => keyPort.portId);

export const toSearchOptions = (ports: PortMessage[], schedule: Schedule[], vesselId?: string): PortOption[] => {
    const portsWithSchedule: SchedulePortOption[] = [];
    const portsWithoutSchedule: NoSchedulePortOption[] = [];

    ports.forEach((port) => {
        const scheduleEntries = schedule.filter((scheduleEntry) => scheduleEntry.port?.id === port.id);
        const label = formatPort({ port, short: true });

        if (scheduleEntries.length) {
            scheduleEntries.forEach((scheduleEntry) => {
                portsWithSchedule.push({
                    type: PortOptionType.SCHEDULE,
                    label,
                    value: scheduleEntry.id,
                    port,
                    vesselId,
                    scheduleEntry,
                    schedulesLength: schedule.length,
                    showReachSeparator: false,
                });
            });
        } else {
            portsWithoutSchedule.push({ type: PortOptionType.NO_SCHEDULE, label, value: port.id, port, vesselId });
        }
    });

    return [...orderScheduleOptionsByReach(portsWithSchedule), ...portsWithoutSchedule];
};
